<template>
    <PlanbordGridContainer :informationColumns="informationColumns" :dayColumns="dayColumns" :hourBalanceColumns="hourBalanceColumns" :compact="viewOptions.showCompactMode">
        <PlanbordHeader
            :roster="roster"
            :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
            :leaveOverviews="leaveOverviews"
            :deploymentCountConfigurations="deploymentCountConfigurations"
            :enabledWorkSchedule="enabledWorkSchedule"
            :workSchedulePositionsPerDay="workSchedulePositionsPerDay"
            @open:workSchedule="$emit('open:workSchedule', $event)"
        />

        <template v-for="(resourceGroup, resourceIndex) in groupedResources" :key="'resource-' + getUniqueResourceKey(resourceGroup.resource)">
            <TitleRow
                v-for="title in titleBeforeResource(resourceIndex)"
                :key="title.id"
                :titleBeforeResource="title"
                :permissions="permissionsForViewedRoster"
                @move:resourceRosterRank="$emit('move:resourceRosterRank', $event)"
                @move:rosterTitleRank="$emit('move:rosterTitleRank', $event)"
            />
            <PlanbordGridRowResource
                :ref="(el: PlanbordGridRow | null) => rosterRows[resourceIndex] = el"
                :roster="roster"
                :rosterNames="rosterNames"
                :resource="resourceGroup.resource"
                :index="resourceIndex"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :leaveOverviews="leaveOverviews"
                :deploymentCounts="deploymentCounts.resources[resourceGroup.resource.id!] ?? []"
                :organisationDeploymentStates="organisationDeploymentStates"
                :contractDeviationLabels="contractDeviationLabels"
                :shifts="shifts"
                :permissions="permissions"
                :showExpandButton="resourceGroup.relatedResources.length > 0"
                :expanded="expandedUserIds[resourceGroup.userId]"
                @toggle:expanded="handleExpanded(resourceGroup.userId, $event)"
                @create:deployment="$emit('create:deployment', $event)"
                @update:deployment="$emit('update:deployment', $event)"
                @delete:deployment="$emit('delete:deployment', $event)"
                @create:stampShift="$emit('create:stampShift', $event)"
                @move:resourceRosterRank="$emit('move:resourceRosterRank', $event)"
                @move:rosterTitleRank="$emit('move:rosterTitleRank', $event)"
            />
            <template
                v-if="expandedUserIds[resourceGroup.userId]"
            >
                <PlanbordGridRowResource
                    v-for="(resource, relatedResourceIndex) in resourceGroup.relatedResources"
                    :key="'other-related-resource-' + getUniqueResourceKey(resource)"
                    :ref="(el: PlanbordGridRow | null) => rosterRows[roster.rosterResources.length + relatedResourceIndex] = el"
                    :roster="roster"
                    :resource="resource"
                    :index="roster.rosterResources.length + relatedResourceIndex"
                    :rosterNames="rosterNames"
                    :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                    :leaveOverviews="leaveOverviews"
                    :deploymentCounts="deploymentCounts.resources[resource.id!] ?? []"
                    :organisationDeploymentStates="organisationDeploymentStates"
                    :contractDeviationLabels="contractDeviationLabels"
                    :shifts="shifts"
                    :permissions="permissions"
                    expanded
                    fromOtherRoster
                    @create:deployment="$emit('create:deployment', $event)"
                    @update:deployment="$emit('update:deployment', $event)"
                    @delete:deployment="$emit('delete:deployment', $event)"
                    @create:stampShift="$emit('create:stampShift', $event)"
                />
            </template>
        </template>

        <TitleRow
            v-for="title in titlesAfterResources()"
            :key="title.id"
            :titleBeforeResource="title"
            :permissions="permissionsForViewedRoster"
            @move:resourceRosterRank="$emit('move:resourceRosterRank', $event)"
            @move:rosterTitleRank="$emit('move:rosterTitleRank', $event)"
        />

        <PlanbordGridRowResource
            v-for="(resource, relatedResourceIndex) in resourcesFromOtherRosters"
            :key="'other-related-resource-' + getUniqueResourceKey(resource)"
            :ref="(el: PlanbordGridRow | null) => rosterRows[roster.rosterResources.length + relatedResourceIndex] = el"
            :roster="roster"
            :resource="resource"
            :index="roster.rosterResources.length + relatedResourceIndex"
            :rosterNames="rosterNames"
            :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
            :leaveOverviews="leaveOverviews"
            :deploymentCounts="deploymentCounts.resources[resource.id!] ?? []"
            :organisationDeploymentStates="organisationDeploymentStates"
            :contractDeviationLabels="contractDeviationLabels"
            :shifts="shifts"
            :permissions="permissions"
            fromOtherRoster
            @create:deployment="$emit('create:deployment', $event)"
            @update:deployment="$emit('update:deployment', $event)"
            @delete:deployment="$emit('delete:deployment', $event)"
            @create:stampShift="$emit('create:stampShift', $event)"
        />

        <template v-for="(company, companyIndex) in enabledCompanies" :key="'company-' + getUniqueCompanyKey(company)">
            <PlanbordGridRowCompany
                :ref="(el: PlanbordGridRow | null) => rosterRows[getCompanyRowIndex(companyIndex, 0)] = el"
                :company="company"
                :index="getCompanyRowIndex(companyIndex, 0)"
                :roster="roster"
                :rosterNames="rosterNames"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :leaveOverviews="leaveOverviews"
                :deploymentCounts="deploymentCounts.companies[company.id] ?? []"
                :organisationDeploymentStates="organisationDeploymentStates"
                :shifts="shifts"
                :permissions="permissions"
                @create:deployment="$emit('create:deployment', $event)"
                @update:deployment="$emit('update:deployment', $event)"
                @delete:deployment="$emit('delete:deployment', $event)"
                @create:stampShift="$emit('create:stampShift', $event)"
                @open:addOpenShiftMarketplaceEmployee="$emit('open:addOpenShiftMarketplaceEmployee', $event)"
            />
            <template v-for="(companyEmployee, companyEmployeeIndex) in Object.values(company.employees)" :key="'company-employee-' + getUniqueCompanyEmployeeKey(companyEmployee)">
                <PlanbordGridRowCompany
                    :ref="(el: PlanbordGridRow | null) => rosterRows[getCompanyRowIndex(companyIndex, companyEmployeeIndex + 1)] = el"
                    :company="company"
                    :companyEmployee="companyEmployee"
                    :index="getCompanyRowIndex(companyIndex, companyEmployeeIndex + 1)"
                    :roster="roster"
                    :rosterNames="rosterNames"
                    :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                    :leaveOverviews="leaveOverviews"
                    :deploymentCounts="[]"
                    :organisationDeploymentStates="organisationDeploymentStates"
                    :shifts="shifts"
                    :permissions="permissions"
                    @create:deployment="$emit('create:deployment', $event)"
                    @update:deployment="$emit('update:deployment', $event)"
                    @delete:deployment="$emit('delete:deployment', $event)"
                    @create:stampShift="$emit('create:stampShift', $event)"
                />
            </template>

        </template>
    </PlanbordGridContainer>
</template>

<script lang="ts">
    import {Title, OrganisationDeploymentState, ContractCaptions, Shift, DeploymentCountConfiguration, LeaveOverview, Deployment} from '@cquential/api';
    import {CellCoordinates, RosterData, PlanbordGridRow, ViewOptions, PermissionsForRoster, ResourceGroup} from '@/types/types';
    import PlanbordHeader from '@/components/Planbord/PlanbordHeader.vue';
    import CellActivator from '@/util/cellactivator';
    import TitleRow from '@/components/Planbord/TitleRow.vue';
    import PlanbordGridRowResource from '@/components/Planbord/PlanbordGridRowResource.vue';
    import PlanbordGridRowCompany from '@/components/Planbord/PlanbordGridRowCompany.vue';
    import {BalanceFilterType} from '@/components/FilterBar/types';
    import {DeploymentCounts} from '@/components/types';
    import {PlanbordResource, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments, PlanbordWorkSchedulePosition, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {ResourceRosterRankUtil} from '@/util/ResourceRosterRankUtil';
    import KeyboardDeploymentAutocomplete from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocomplete.vue';
    import PlanbordGridContainer from '@/components/Planbord/PlanbordGridContainer.vue';
    import {Component, Vue, Prop, Watch} from 'vue-facing-decorator';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';

    @Component({
        components: {
            PlanbordHeader,
            TitleRow,
            PlanbordGridRowResource,
            PlanbordGridRowCompany,
            PlanbordGridContainer,
        },
        emits: [
            'open:workSchedule',
            'open:addOpenShiftMarketplaceEmployee',
            'move:resourceRosterRank',
            'move:rosterTitleRank',
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:stampShift',
        ],
    })
    export default class PlanbordGrid extends Vue {
        @Prop({required: true}) public viewOptions!: ViewOptions;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public enabledCompanies!: CompanyWithDeploymentsAndEmployees[];
        @Prop({required: true}) public enabledBalanceAndCountDisplays!: Record<BalanceFilterType, Record<number, boolean>>;
        @Prop({required: true}) public leaveOverviews!: LeaveOverview[];
        @Prop({required: true}) public deploymentCountConfigurations!: DeploymentCountConfiguration[];
        @Prop({required: true}) public deploymentCounts!: DeploymentCounts;
        @Prop({type: Number, required: true}) public enabledHourColumnCount!: number;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop() public contractDeviationLabels?: ContractCaptions;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public permissions!: Record<number, PermissionsForRoster>;
        @Prop({type: Boolean}) public enabledWorkSchedule!: boolean;
        @Prop({type: Number, required: true}) public informationColumns!: number;
        @Prop({type: Number, required: true}) public dayColumns!: number;
        @Prop({type: Number, required: true}) public hourBalanceColumns!: number;
        @Prop({required: true}) public workSchedulePositionsPerDay!: Record<string, PlanbordWorkSchedulePosition[]>;

        private static instance: PlanbordGrid | null;

        private activeCell: CellCoordinates | null = null;
        private selectedCells: CellCoordinates[] = [];
        private firstSelectedCell: CellCoordinates | null = null;
        private lastSelectedCell: CellCoordinates | null = null;
        private isMouseMultiSelectActive: boolean = false;
        private rosterRows: Record<number, PlanbordGridRow | null> = {};
        private expandedUserIds: Record<string, boolean> = {};

        @Watch('activeCell')
        @Watch('selectedCells')
        public markCells(): void {
            if (this.activeCell) {
                CellActivator.activateCell(this.activeCell);
            }
            CellActivator.deselectAllCells();
            this.selectedCells.forEach(CellActivator.selectCell);
        }

        public get groupedResources(): ResourceGroup[] {
            const relatedResourcesPerUserId: Record<string, ResourceWithKeyedByDateObjects[]> = {};
            const relatedResourcesWithoutUser: Record<number, ResourceWithKeyedByDateObjects[]> = {};

            for (const resource of this.roster.relatedResources.concat(this.roster.resourcesFromOtherRosters)) {
                const userId: number | undefined = resource.employment?.userId;
                if (!userId) {
                    relatedResourcesWithoutUser[resource.employmentId] ??= [];
                    relatedResourcesWithoutUser[resource.employmentId].push(resource);
                } else {
                    relatedResourcesPerUserId[userId] ??= [];
                    relatedResourcesPerUserId[userId].push(resource);
                }
            }

            return this.roster.rosterResources.map((resource: PlanbordResource): ResourceGroup => {
                const userId: number | undefined = resource.employment?.userId;
                let relatedResources: ResourceWithKeyedByDateObjects[] = [];

                if (userId && userId in relatedResourcesPerUserId) {
                    relatedResources = relatedResources.concat(relatedResourcesPerUserId[userId]);
                }

                if (resource.employmentId in relatedResourcesWithoutUser) {
                    relatedResources = relatedResources.concat(relatedResourcesWithoutUser[resource.employmentId]);
                }

                return {resource, relatedResources, userId: userId ? userId : -resource.employmentId};
            });
        }

        public get resourceIdsFromGroupedResources(): Record<number, true> {
            const result: Record<number, true> = {};

            for (const resourceGroup of this.groupedResources) {
                result[resourceGroup.resource.id!] = true;
                for (const relatedResource of resourceGroup.relatedResources) {
                    result[relatedResource.id!] = true;
                }
            }

            return result;
        }

        public get resourcesFromOtherRosters(): ResourceWithKeyedByDateObjects[] {
            return this.roster.resourcesFromOtherRosters.filter((resource: ResourceWithKeyedByDateObjects): boolean => !(resource.id! in this.resourceIdsFromGroupedResources));
        }

        public get permissionsForViewedRoster(): PermissionsForRoster {
            return this.permissions[this.roster.rosterId];
        }

        public mounted(): void {
            // End the multiselect whenever you blur the browser window/tab
            window.addEventListener('blur', this.deactivateMouseMultiSelect);
            // End the multiselect when the mouseclick is released
            window.addEventListener('mouseup', this.deactivateMouseMultiSelect);
            PlanbordGrid.instance = this;
        }

        public beforeUnmount(): void {
            PlanbordGrid.instance = null;

            window.removeEventListener('blur', this.deactivateMouseMultiSelect);
            window.removeEventListener('mouseup', this.deactivateMouseMultiSelect);
        }

        public updated(): void {
            this.markCells();
        }

        public handleExpanded(userId: number, expanded: boolean): void {
            this.expandedUserIds[userId] = expanded;
        }

        public sortCellCoordinates(coordinates: CellCoordinates[]): CellCoordinates[] {
            return coordinates.sort((a: CellCoordinates, b: CellCoordinates): number => {
                return a.y - b.y || a.x - b.x;
            });
        }

        public getUniqueResourceKey(resource: ResourceWithKeyedByDateObjects): string {
            let key: string = resource.id!.toString();
            if (Object.keys(resource.deployments).length) {
                key += '-deployment-';
                for (const deployments of Object.values(resource.deployments)) {
                    key += deployments.map((deployment: Deployment): number => deployment.id!).join('-');
                }
            }

            return key;
        }

        public getUniqueCompanyKey(company: CompanyWithDeploymentsAndEmployees): string {
            let key: string = company.id.toString();
            if (company.deploymentsWithoutEmployee.length) {
                key += '-deployments-';
                for (const deployments of Object.values(company.deploymentsWithoutEmployee)) {
                    key += deployments.map((deployment: Deployment): number => deployment.id!).join('-');
                }
            }

            return key;
        }

        public getUniqueCompanyEmployeeKey(employee: CompanyEmployeeWithDeployments): string {
            let key: string = employee.id!.toString();
            if (employee.deployments.length) {
                key += '-deployments-';
                for (const deployments of Object.values(employee.deployments)) {
                    key += deployments.map((deployment: Deployment): number => deployment.id!).join('-');
                }
            }

            return key;
        }

        public getCompanyRowIndex(companyIndex: number, companyEmployeeIndex: number): number {
            const firstIndex: number = this.roster.rosterResources.length + this.roster.resourcesFromOtherRosters.length + this.roster.relatedResources.length;

            if (companyIndex === 0) {
                return firstIndex + companyEmployeeIndex;
            }
            const previousCompanies: CompanyWithDeploymentsAndEmployees[] = Object.values(this.enabledCompanies).slice(0, companyIndex);
            const previousCompaniesEmployeeCount: number = previousCompanies
                .map((company: CompanyWithDeploymentsAndEmployees): number => Object.values(company.employees).length)
                .reduce((total: number, current: number): number => total + current, 0);

            return firstIndex + companyIndex + previousCompaniesEmployeeCount + companyEmployeeIndex;
        }

        public getRow(position: number): PlanbordGridRow | undefined {
            if (this.rosterRows[position] === null) {
                return undefined;
            }

            return this.rosterRows[position];
        }

        public getPlanbordCellsFromCoordinates(cells: CellCoordinates[]): PlanbordCell[] {
            const planbordCells: PlanbordCell[] = [];
            for (const cell of cells) {
                const planbordCell: PlanbordCell | undefined = this.getRow(cell.y)?.getCell(cell.x);
                if (planbordCell) {
                    planbordCells.push(planbordCell);
                }
            }

            return planbordCells;
        }

        public selectCell(cell: CellCoordinates): void {
            this.deselectAllCells();
            this.selectCellsUntilGivenCell(cell);
        }

        public deselectCell(data: CellCoordinates): void {
            this.selectedCells = this.selectedCells.filter((cell: CellCoordinates): boolean =>
                cell.x !== data.x || cell.y !== data.y,
            );

            if (this.selectedCells.length === 1) {
                this.selectedCells = [];
            }
        }

        public deselectAllCells(): void {
            if (this.selectedCells.length > 0) {
                this.selectedCells = [];
            }
        }

        public activateMouseMultiSelect(cell: CellCoordinates): void {
            this.isMouseMultiSelectActive = true;
            this.selectCell(cell);
        }

        public deactivateMouseMultiSelect(): void {
            this.isMouseMultiSelectActive = false;
        }

        public resetMultiSelect(): void {
            this.firstSelectedCell = null;
            this.lastSelectedCell = null;
        }

        public resetActiveCell(): void {
            /** @see KeyboardDeploymentAutocomplete.disable() */
            // @ts-expect-error TS2339
            KeyboardDeploymentAutocomplete.__vfdConstructor.disable();
            // In case of a multi select, reset to the last selected cell
            if (this.lastSelectedCell !== null && this.lastSelectedCell !== this.activeCell) {
                this.activeCell = this.lastSelectedCell;

                return;
            }
            this.activeCell = null;
        }

        public isCellAlreadySelected(cell: CellCoordinates): boolean {
            return this.selectedCells.some((alreadySelectedCell: CellCoordinates): boolean =>
                alreadySelectedCell.x === cell.x && alreadySelectedCell.y === cell.y,
            );
        }

        public selectCellsUntilGivenCell(cell: CellCoordinates): void {
            if (this.firstSelectedCell === null) {
                // Use the active cell instead of the given cell, this ensures consistent behaviour on keyboard selection.
                this.firstSelectedCell = this.activeCell || cell;
            }

            this.lastSelectedCell = cell;

            // Always add cells from top left corner to top right corner, regardless of selection direction
            const xStart: number = Math.min(this.firstSelectedCell.x, this.lastSelectedCell.x);
            const xTarget: number = Math.max(this.firstSelectedCell.x, this.lastSelectedCell.x);

            const yStart: number = Math.min(this.firstSelectedCell.y, this.lastSelectedCell.y);
            const yTarget: number = Math.max(this.firstSelectedCell.y, this.lastSelectedCell.y);

            this.multiSelectCells({x: xStart, y: yStart}, {x: xTarget, y: yTarget});
        }

        public multiSelectCells(start: CellCoordinates, target: CellCoordinates, mode: 'select' | 'deselect' = 'select'): void {
            for (let yPos: number = start.y; yPos <= target.y; yPos++) {
                for (let xPos: number = start.x; xPos <= target.x; xPos++) {
                    if (mode === 'select' && !this.isCellAlreadySelected({x: xPos, y: yPos})) {
                        this.selectedCells.push({x: xPos, y: yPos});
                    }
                    if (mode === 'deselect' && this.isCellAlreadySelected({x: xPos, y: yPos})) {
                        this.deselectCell({x: xPos, y: yPos});
                    }
                }
            }
        }

        public titleBeforeResource(index: number): Title[] {
            if (!this.roster.titles || !this.roster.rosterResources[index]) {
                return [];
            }

            const previousResourceRankInRoster: number = index > 0 ? this.roster.rosterResources[index - 1].rankInRoster : -1;
            const resourceRankInRoster: number = this.roster.rosterResources[index].rankInRoster;

            return this.roster.titles.filter((title: Title): boolean => title.rankInRoster > previousResourceRankInRoster && title.rankInRoster <= resourceRankInRoster);
        }

        public titlesAfterResources(): Title[] {
            if (!this.roster || !this.roster.titles) {
                return [];
            }
            const highestResourceRank: number = ResourceRosterRankUtil.getHighestResourceRank(this.roster);

            return this.roster.titles.filter((title: Title): boolean => title.rankInRoster > highestResourceRank);
        }

        // Static wrapper methods for grid proxy functionality
        public static getActiveCell(): CellCoordinates | null {
            return this.instance!.activeCell;
        }

        public static setActiveCell(cell: CellCoordinates | null): void {
            this.instance!.activeCell = cell;
        }

        public static getFirstSelectedCell(): CellCoordinates | null {
            return this.instance!.firstSelectedCell;
        }

        public static getLastSelectedCell(): CellCoordinates | null {
            return this.instance!.lastSelectedCell;
        }

        public static getSelectedCells(): CellCoordinates[] {
            return this.instance!.selectedCells;
        }

        public static setSelectedCells(cells: CellCoordinates[]): void {
            this.instance!.selectedCells = cells;
        }

        public static getIsMouseMultiSelectActive(): boolean {
            return this.instance!.isMouseMultiSelectActive;
        }

        public static isCellAlreadySelected(cell: CellCoordinates): boolean {
            return this.instance!.isCellAlreadySelected(cell);
        }

        public static activateMouseMultiSelect(cell: CellCoordinates): void {
            this.instance!.activateMouseMultiSelect(cell);
        }

        public static selectCell(cell: CellCoordinates | null): void {
            if (cell) {
                this.instance!.selectCell(cell);
            }
        }

        public static resetActiveCell(): void {
            this.instance!.resetActiveCell();
        }

        public static deselectAllCells(): void {
            this.instance!.deselectAllCells();
        }

        public static resetGrid(): void {
            this.instance!.resetMultiSelect();
            this.instance!.deselectAllCells();
            this.instance!.resetActiveCell();
        }

        public static getRow(position: number): PlanbordGridRow | undefined {
            return this.instance!.getRow(position);
        }

        public static getPlanbordCellsFromCoordinates(cells: CellCoordinates[]): PlanbordCell[] {
            return this.instance!.getPlanbordCellsFromCoordinates(cells);
        }

        public static sortCellCoordinates(cells: CellCoordinates[]): CellCoordinates[] {
            return this.instance!.sortCellCoordinates(cells);
        }
    }
</script>

<style lang="scss" scoped>
    table {
        margin-bottom: 2px;
    }
</style>
